import { useEffect, useRef } from "react";

const DEFAULT_POLL_INTERVAL_MS = 60_000;

/**
 * Runs a callback periodically only when the document (tab) is visible.
 * Stops polling when the tab is hidden; resumes when it becomes visible again.
 * Callback runs immediately on mount (if visible) and whenever it changes.
 *
 * @param {() => void | Promise<void>} callback - Function to run (e.g. fetch data)
 * @param {number} [intervalMs] - Poll interval in milliseconds (default: 60000)
 */
export function useVisibilityAwarePolling(callback, intervalMs = DEFAULT_POLL_INTERVAL_MS) {
  const intervalRef = useRef(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const run = () => {
      if (document.visibilityState === "visible") {
        callbackRef.current();
      }
    };

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(run, intervalMs);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        run();
        startPolling();
      } else {
        stopPolling();
      }
    };

    run();
    if (document.visibilityState === "visible") {
      startPolling();
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPolling();
    };
  }, [intervalMs, callback]);
}
